// US1945335: Global Feature Toggle API client
import { authFetch } from "../utils/authFetch";

const BASE = "/api/v1/global-config/feature-toggles";

/**
 * Fetch all global feature toggles.
 *
 * @param {Object} options - Fetch options (supports AbortController signal)
 * @returns {Promise<{toggles: Object}>} Map of toggle name → toggle state object
 * @throws {Error} On non-OK HTTP response
 */
export async function fetchFeatureToggles(options = {}) {
  const { headers: callerHeaders, signal, ...rest } = options;
  const response = await authFetch(BASE, {
    method: "GET",
    ...rest,
    signal,
    headers: {
      "X-Requested-With": "XMLHttpRequest",
      ...callerHeaders,
    },
  });
  if (!response.ok) {
    throw new Error(`fetchFeatureToggles failed: ${response.status}`);
  }
  return response.json();
}

/**
 * Patch a single global feature toggle's enabled state.
 *
 * The backend requires X-Requested-With for CSRF protection on mutating
 * requests. Setting it on GET is defence-in-depth only.
 *
 * @param {string} name - Toggle name (URL-encoded automatically)
 * @param {boolean} enabled - New enabled state
 * @returns {Promise<{toggles: Object}>} Updated toggle map
 * @throws {Error} On non-OK HTTP response
 */
export async function patchFeatureToggle(name, enabled) {
  const response = await authFetch(
    `${BASE}/${encodeURIComponent(name)}`,
    {
      method: "PATCH",
      headers: {
        "Content-Type": "application/json",
        "X-Requested-With": "XMLHttpRequest",
      },
      body: JSON.stringify({ enabled }),
    }
  );

  if (!response.ok) {
    let body = {};
    try {
      body = await response.json();
    } catch (_) {
      // ignore parse error; use fallback message
    }
    const err = new Error(
      body?.error?.message || `patchFeatureToggle failed: ${response.status}`
    );
    err.code = body?.error?.code || `HTTP_${response.status}`;
    err.status = response.status;
    throw err;
  }

  return response.json();
}
